import { API_BASE } from '../config';

function mustToken(token) {
  const t = token || localStorage.getItem('token');
  if (!t) {
    const err = new Error('You must be logged in to continue.');
    err.status = 401;
    throw err;
  }
  return t;
}

async function parseJsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function opsFetch(path, { token, method = 'GET', body } = {}) {
  const authToken = mustToken(token);
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${authToken}`,
      ...(body != null ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body != null ? { body: JSON.stringify(body) } : {}),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    const msg =
      data?.error ||
      data?.message ||
      (Array.isArray(data?.errors) ? data.errors.map((e) => e.msg || e).join(', ') : null) ||
      `Request failed (HTTP ${res.status}).`;
    const err = new Error(msg);
    err.status = res.status;
    err.detail = data?.detail || '';
    throw err;
  }
  return data;
}

const OPS_ME_CACHE_MS = 30000;
const OPS_ME_NEG_CACHE_MS = 8000;
const OPS_ME_429_BACKOFF_MS = 20000;
let opsMeCache = { at: 0, data: null, promise: null, negUntil: 0, negError: null };

export function invalidateOpsMeCache() {
  opsMeCache = { at: 0, data: null, promise: null, negUntil: 0, negError: null };
  opsSummaryCache = { at: 0, data: null, promise: null };
}

export function isOnboardingAdminNotConfiguredError(err) {
  const msg = String(err?.message || '');
  return err?.status === 503 && /not configured/i.test(msg) && /ONBOARDING_ADMIN_API/i.test(msg);
}

export async function fetchOpsMe({ token, fresh = false } = {}) {
  const now = Date.now();
  if (!fresh && opsMeCache.data && now - opsMeCache.at < OPS_ME_CACHE_MS) {
    return opsMeCache.data;
  }
  if (!fresh && opsMeCache.negUntil > now && opsMeCache.negError) {
    throw opsMeCache.negError;
  }
  if (!fresh && opsMeCache.promise) {
    return opsMeCache.promise;
  }
  opsMeCache.promise = opsFetch('/api/ops/me', { token })
    .then((data) => {
      opsMeCache.data = data;
      opsMeCache.at = Date.now();
      opsMeCache.promise = null;
      opsMeCache.negUntil = 0;
      opsMeCache.negError = null;
      return data;
    })
    .catch((err) => {
      opsMeCache.promise = null;
      const status = err?.status;
      if (status === 401 || status === 403) {
        opsMeCache.data = null;
        opsMeCache.at = 0;
        opsMeCache.negError = err;
        opsMeCache.negUntil = Date.now() + OPS_ME_NEG_CACHE_MS;
      } else if (status === 429) {
        opsMeCache.negError = err;
        opsMeCache.negUntil = Date.now() + OPS_ME_429_BACKOFF_MS;
      }
      throw err;
    });
  return opsMeCache.promise;
}

export async function fetchOpsHealth() {
  const res = await fetch(`${API_BASE}/api/health`);
  const data = await parseJsonSafe(res);
  return {
    ok: !!data?.ok,
    status: res.status,
    mongo: data?.mongo || 'unknown',
    message: data?.message || '',
    lastConnectError: data?.lastConnectError || null,
  };
}

export async function fetchOpsTeamPermissions() {
  const data = await opsFetch('/api/ops/team/permissions');
  return {
    roles: data?.roles || [],
    permissions: data?.permissions || [],
    roleDefaults: data?.roleDefaults || {},
  };
}

export async function fetchOpsTeam() {
  const data = await opsFetch('/api/ops/team');
  return data?.members || [];
}

export async function createOpsTeamMember({ email, name, role, permissions, notes, password }) {
  return opsFetch('/api/ops/team', {
    method: 'POST',
    body: { email, name, role, permissions, notes, password },
  });
}

export async function patchOpsTeamMember({ id, ...payload }) {
  const data = await opsFetch(`/api/ops/team/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });
  return data?.member || null;
}

export async function importOpsTeamFromEnv() {
  return opsFetch('/api/ops/team/import-env', { method: 'POST' });
}

export async function fetchOpsDoctors() {
  const data = await opsFetch('/api/ops/doctors');
  return data?.doctors || [];
}

export async function fetchOpsDoctor(id) {
  const data = await opsFetch(`/api/ops/doctors/${encodeURIComponent(id)}`);
  return data?.doctor || null;
}

export async function createOpsDoctor({ email, name, notes, password }) {
  return opsFetch('/api/ops/doctors', {
    method: 'POST',
    body: { email, name, notes, password },
  });
}

export async function patchOpsDoctor({ id, ...payload }) {
  const data = await opsFetch(`/api/ops/doctors/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });
  return data?.doctor || null;
}

export async function patchOpsDoctorProfile({ id, ...payload }) {
  return patchOpsDoctor({ id, ...payload });
}

export async function uploadOpsDoctorDocument({ id, docType, file }) {
  const authToken = mustToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(
    `${API_BASE}/api/ops/doctors/${encodeURIComponent(id)}/documents/${encodeURIComponent(docType)}`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
      body: form,
    }
  );
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    const err = new Error(data?.error || `Upload failed (HTTP ${res.status}).`);
    err.status = res.status;
    throw err;
  }
  return data?.doctor || null;
}

export async function deleteOpsDoctorDocument({ id, docType }) {
  const data = await opsFetch(
    `/api/ops/doctors/${encodeURIComponent(id)}/documents/${encodeURIComponent(docType)}`,
    { method: 'DELETE' }
  );
  return data?.doctor || null;
}

export function opsDoctorDocumentUrl({ id, docType }) {
  return `${API_BASE}/api/ops/doctors/${encodeURIComponent(id)}/documents/${encodeURIComponent(docType)}`;
}

export async function openOpsDoctorDocument({ id, docType }) {
  const authToken = mustToken();
  const res = await fetch(opsDoctorDocumentUrl({ id, docType }), {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!res.ok) {
    const data = await parseJsonSafe(res);
    throw new Error(data?.error || `Download failed (HTTP ${res.status}).`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export async function importOpsDoctorsFromEnv() {
  return opsFetch('/api/ops/doctors/import-env', { method: 'POST' });
}

const UNIFIED_SUMMARY_CACHE_MS = 5 * 60 * 1000;
let unifiedSummaryCache = { at: 0, data: null, promise: null };

export function invalidateUnifiedUsersSummaryCache() {
  unifiedSummaryCache = { at: 0, data: null, promise: null };
}

export async function fetchUnifiedUsersSummary({ fresh = false } = {}) {
  const now = Date.now();
  if (!fresh && unifiedSummaryCache.data && now - unifiedSummaryCache.at < UNIFIED_SUMMARY_CACHE_MS) {
    return unifiedSummaryCache.data;
  }
  if (!fresh && unifiedSummaryCache.promise) {
    return unifiedSummaryCache.promise;
  }
  unifiedSummaryCache.promise = opsFetch('/api/ops/unified-users/summary')
    .then((data) => {
      unifiedSummaryCache.data = data;
      unifiedSummaryCache.at = Date.now();
      unifiedSummaryCache.promise = null;
      return data;
    })
    .catch((err) => {
      unifiedSummaryCache.promise = null;
      throw err;
    });
  return unifiedSummaryCache.promise;
}

export async function sendWelcomeEmail({ userType, email, name, tempPassword, loginUrl }) {
  return opsFetch('/api/ops/users/send-welcome', {
    method: 'POST',
    body: { userType, email, name, tempPassword, loginUrl },
  });
}

export async function fetchInviteText({ userType, email, name, tempPassword, loginUrl }) {
  return opsFetch('/api/ops/users/invite-text', {
    method: 'POST',
    body: { userType, email, name, tempPassword, loginUrl },
  });
}

const OPS_LIST_CACHE_MS = 5 * 60 * 1000;
let platformUsersCache = { at: 0, data: null, stale: false };
let hrUsersCache = { at: 0, data: null, stale: false };
let membersCache = { at: 0, data: null, stale: false };

export async function fetchPlatformUsers({ fresh = false } = {}) {
  if (!fresh && platformUsersCache.data && Date.now() - platformUsersCache.at < OPS_LIST_CACHE_MS) {
    return { users: platformUsersCache.data, stale: !!platformUsersCache.stale };
  }
  const data = await opsFetch('/api/ops/unified-users/platform');
  const users = data?.users || [];
  const stale = !!data?.stale;
  platformUsersCache = { at: Date.now(), data: users, stale };
  return { users, stale };
}

export async function createPlatformUser({ email, password, role, name }) {
  invalidateUnifiedUsersSummaryCache();
  platformUsersCache = { at: 0, data: null, stale: false };
  return opsFetch('/api/ops/unified-users/platform', {
    method: 'POST',
    body: { email, password, role, name },
  });
}

export async function patchPlatformUser({ id, ...payload }) {
  invalidateUnifiedUsersSummaryCache();
  platformUsersCache = { at: 0, data: null, stale: false };
  const data = await opsFetch(`/api/ops/unified-users/platform/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });
  return data?.user || null;
}

export async function fetchHrUsers({ fresh = false } = {}) {
  if (!fresh && hrUsersCache.data && Date.now() - hrUsersCache.at < OPS_LIST_CACHE_MS) {
    return { users: hrUsersCache.data, stale: !!hrUsersCache.stale };
  }
  const data = await opsFetch('/api/ops/unified-users/hr');
  const users = data?.users || [];
  const stale = !!data?.stale;
  hrUsersCache = { at: Date.now(), data: users, stale };
  return { users, stale };
}

export async function patchHrUser({ sponsorId, spocIndex, active, name, email, hrLoginEmail }) {
  invalidateUnifiedUsersSummaryCache();
  hrUsersCache = { at: 0, data: null, stale: false };
  const body = { sponsorId, spocIndex, active, name, email };
  if (hrLoginEmail !== undefined) body.hrLoginEmail = hrLoginEmail;
  const data = await opsFetch('/api/ops/unified-users/hr', {
    method: 'PATCH',
    body,
  });
  return data?.user || null;
}

export async function fetchOpsMembers({ fresh = false } = {}) {
  if (!fresh && membersCache.data && Date.now() - membersCache.at < OPS_LIST_CACHE_MS) {
    return { members: membersCache.data, stale: !!membersCache.stale };
  }
  const data = await opsFetch('/api/ops/members');
  const members = data?.members || [];
  const stale = !!data?.stale;
  membersCache = { at: Date.now(), data: members, stale };
  return { members, stale };
}

export async function fetchOpsDigitization({ status = 'all', type = 'all', q = '', limit = 100 } = {}) {
  const params = new URLSearchParams();
  if (status && status !== 'all') params.set('status', status);
  if (type && type !== 'all') params.set('type', type);
  if (q) params.set('q', q);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  const data = await opsFetch(`/api/ops/digitization${qs ? `?${qs}` : ''}`);
  return data?.requests || [];
}

export async function fetchOpsDigitizationById(id) {
  const data = await opsFetch(`/api/ops/digitization/${encodeURIComponent(id)}`);
  return data?.request || null;
}

export async function cancelOpsDigitization(id, { reason, notifyMember = true } = {}) {
  const data = await opsFetch(`/api/ops/digitization/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    body: { reason, notifyMember },
  });
  return data?.request || null;
}

export async function fetchMemberOrders(email) {
  return opsFetch(`/api/ops/members/${encodeURIComponent(email)}/orders`);
}

export async function setMemberPassword(email, password, { sendWelcome = true } = {}) {
  return opsFetch(`/api/ops/members/${encodeURIComponent(email)}/password`, {
    method: 'PATCH',
    body: { password, sendWelcome },
  });
}

export async function provisionMember(email) {
  return opsFetch(`/api/ops/members/${encodeURIComponent(email)}/provision`, {
    method: 'POST',
  });
}

export async function fetchEmployeeMailStatus(sponsorId) {
  const data = await opsFetch(
    `/api/ops/sponsors/${encodeURIComponent(sponsorId)}/employee-mail-status`
  );
  return data;
}

export async function resendEmployeeMailBatch(sponsorId, emails) {
  return opsFetch(`/api/ops/sponsors/${encodeURIComponent(sponsorId)}/employee-mail-resend`, {
    method: 'POST',
    body: { emails },
  });
}

export async function fetchContactEnquiries() {
  const data = await opsFetch('/api/ops/enquiries/contact');
  return data?.submissions || [];
}

export async function fetchDemoEnquiries() {
  const data = await opsFetch('/api/ops/enquiries/demo');
  return data?.submissions || [];
}

export async function inviteHrUserPassword({ email }) {
  return opsFetch('/api/ops/unified-users/hr/invite-password', {
    method: 'POST',
    body: { email },
  });
}

export const OPS_ROLE_LABELS = {
  associate: 'Associate',
  sr_associate: 'Sr Associate',
  tl: 'Team Lead',
  admin: 'Admin',
};

export const OPS_PERMISSION_LABELS = {
  'bookings.view': 'View bookings',
  'bookings.edit': 'Edit bookings',
  'bookings.export': 'Export bookings',
  'bookings.stage': 'Update ops stage',
  'master.view': 'Master dashboard',
  'service_areas.view': 'View service areas',
  'service_areas.edit': 'Edit service areas',
  'payments.view': 'View lab payments',
  'refunds.manage': 'Manage refunds',
  'partners.view': 'View partners',
  'sponsors.view': 'View sponsors',
  'team.manage': 'Manage ops team',
  'orders.partner_change': 'Change partner',
  'orders.manual_create': 'Manual booking',
  'voc.manage': 'VOC',
  'escalations.manage': 'Escalations',
};

const OPS_SUMMARY_CACHE_MS = 30 * 1000;
let opsSummaryCache = { at: 0, data: null, promise: null };

/** OpsLayout and OpsDashboard both load the summary on mount; share one request. */
export async function fetchOpsSummary({ token, fresh = false } = {}) {
  const now = Date.now();
  if (!fresh && opsSummaryCache.data && now - opsSummaryCache.at < OPS_SUMMARY_CACHE_MS) {
    return opsSummaryCache.data;
  }
  if (!fresh && opsSummaryCache.promise) return opsSummaryCache.promise;

  const promise = opsFetch('/api/ops/summary', { token })
    .then((data) => {
      const summary = data?.summary || null;
      opsSummaryCache = { at: Date.now(), data: summary, promise: null };
      return summary;
    })
    .catch((err) => {
      opsSummaryCache = { ...opsSummaryCache, promise: null };
      throw err;
    });
  opsSummaryCache = { ...opsSummaryCache, promise };
  return promise;
}

export async function fetchOpsAnalytics({ month, token } = {}) {
  const q = month ? `?month=${encodeURIComponent(month)}` : '';
  const data = await opsFetch(`/api/ops/analytics${q}`, { token });
  return data?.analytics || null;
}

export async function fetchMarketingSummary(sponsorId) {
  return opsFetch(
    `/api/ops/marketing/summary?sponsorId=${encodeURIComponent(sponsorId)}`
  );
}

export async function fetchMarketingEmployees({ sponsorId, segment = 'all', q = '', sort = 'name' } = {}) {
  const params = new URLSearchParams({
    sponsorId,
    segment,
    sort,
  });
  if (q) params.set('q', q);
  return opsFetch(`/api/ops/marketing/employees?${params.toString()}`);
}

export async function sendMarketingBatch({ sponsorId, campaignType, emails, force = false }) {
  return opsFetch('/api/ops/marketing/send-batch', {
    method: 'POST',
    body: { sponsorId, campaignType, emails, force },
  });
}

export async function fetchMarketingAttempts({ sponsorId, campaignType, status, limit } = {}) {
  const params = new URLSearchParams({ sponsorId });
  if (campaignType) params.set('campaignType', campaignType);
  if (status) params.set('status', status);
  if (limit) params.set('limit', String(limit));
  const data = await opsFetch(`/api/ops/marketing/attempts?${params.toString()}`);
  return data?.attempts || [];
}

export async function fetchCampaignTemplate(campaignType) {
  const params = new URLSearchParams({ campaignType: campaignType || 'activation_nudge' });
  const data = await opsFetch(`/api/ops/marketing/campaign-template?${params.toString()}`);
  return data?.promo || null;
}

export async function saveCampaignTemplate(campaignType, payload) {
  const data = await opsFetch('/api/ops/marketing/campaign-template', {
    method: 'PUT',
    body: { campaignType, ...payload },
  });
  return data?.promo || null;
}

export async function fetchFridayPromo() {
  return fetchCampaignTemplate('friday_awareness');
}

export async function saveFridayPromo(payload) {
  return saveCampaignTemplate('friday_awareness', payload);
}

export async function uploadFridayPromoAttachment(file) {
  const authToken = mustToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE}/api/ops/marketing/friday-promo/attachment`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${authToken}` },
    body: form,
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    const err = new Error(data?.error || `Upload failed (HTTP ${res.status}).`);
    err.status = res.status;
    throw err;
  }
  return data?.promo || null;
}

export async function clearFridayPromoAttachment() {
  const data = await opsFetch('/api/ops/marketing/friday-promo/attachment', {
    method: 'DELETE',
  });
  return data?.promo || null;
}

export async function fetchOpsMasterSummary({ token } = {}) {
  const data = await opsFetch('/api/ops/master/summary', { token });
  return data?.master || null;
}

export async function fetchOpsPartnerPerformance({ token } = {}) {
  const data = await opsFetch('/api/ops/master/partner-performance', { token });
  return data?.partners || [];
}

export async function fetchOpsServiceAreas({ token } = {}) {
  const data = await opsFetch('/api/ops/service-areas', { token });
  return { areas: data?.areas || [], services: data?.services || [] };
}

export async function createOpsServiceArea(body, { token } = {}) {
  const data = await opsFetch('/api/ops/service-areas', { token, method: 'POST', body });
  return data?.area || null;
}

export async function patchOpsServiceArea({ token, slug, service, status, message }) {
  const data = await opsFetch(`/api/ops/service-areas/${encodeURIComponent(slug)}`, {
    token,
    method: 'PATCH',
    body: { service, status, message },
  });
  return data?.area || null;
}

export async function seedOpsServiceAreas({ token } = {}) {
  return opsFetch('/api/ops/service-areas/seed', { token, method: 'POST' });
}

function buildOrdersQs(params) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v != null && v !== '') qs.set(k, String(v));
  });
  return qs.toString();
}

export async function fetchOpsOrders(params = {}) {
  const data = await opsFetch(`/api/ops/orders?${buildOrdersQs(params)}`);
  return {
    orders: data?.orders || [],
    stages: data?.stages || [],
    pagination: data?.pagination || null,
    filterMeta: data?.filterMeta || null,
  };
}

export async function exportOpsOrdersCsv(params = {}) {
  const token = mustToken();
  const res = await fetch(`${API_BASE}/api/ops/orders/export?${buildOrdersQs(params)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = await parseJsonSafe(res);
    throw new Error(data?.error || 'Export failed');
  }
  return res.blob();
}

export async function fetchOpsOrder({ token, id }) {
  const data = await opsFetch(`/api/ops/orders/${encodeURIComponent(id)}`, { token });
  return { order: data?.order || null, activities: data?.activities || [], stages: data?.stages || [] };
}

export async function patchOpsOrder({ token, id, status, cancelReason }) {
  const data = await opsFetch(`/api/ops/orders/${encodeURIComponent(id)}`, {
    token,
    method: 'PATCH',
    body: { status, cancelReason },
  });
  return data?.order || null;
}

export async function patchOpsOrderStage({ token, id, opsStage }) {
  const data = await opsFetch(`/api/ops/orders/${encodeURIComponent(id)}/stage`, {
    token,
    method: 'PATCH',
    body: { opsStage },
  });
  return data?.order || null;
}

export async function patchOpsOrderFlags({ token, id, flags }) {
  const data = await opsFetch(`/api/ops/orders/${encodeURIComponent(id)}/flags`, {
    token,
    method: 'PATCH',
    body: flags,
  });
  return data?.order || null;
}

export async function logOpsActivity({ token, id, action, summary, meta }) {
  const data = await opsFetch(`/api/ops/orders/${encodeURIComponent(id)}/activity`, {
    token,
    method: 'POST',
    body: { action, summary, meta },
  });
  return data?.activity || null;
}

export async function rescheduleOpsOrder({ token, id, appointmentDate, appointmentTime }) {
  const data = await opsFetch(`/api/ops/orders/${encodeURIComponent(id)}/reschedule`, {
    token,
    method: 'PATCH',
    body: { appointmentDate, appointmentTime },
  });
  return data?.order || null;
}

export async function changeOpsOrderPartner({ token, id, partnerName, centreName }) {
  const data = await opsFetch(`/api/ops/orders/${encodeURIComponent(id)}/partner`, {
    token,
    method: 'PATCH',
    body: { partnerName, centreName },
  });
  return data?.order || null;
}

export async function uploadOpsOrderReport({ token, id, type, fileName, fileUrl }) {
  const data = await opsFetch(`/api/ops/orders/${encodeURIComponent(id)}/reports`, {
    token,
    method: 'POST',
    body: { type, fileName, fileUrl },
  });
  return data?.order || null;
}

export async function uploadOpsOrderReportFile({ id, type, file }) {
  const authToken = mustToken();
  const form = new FormData();
  form.append('file', file);
  form.append('type', type);
  const res = await fetch(
    `${API_BASE}/api/ops/orders/${encodeURIComponent(id)}/reports/upload`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
      body: form,
    }
  );
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    const err = new Error(data?.error || `Upload failed (HTTP ${res.status}).`);
    err.status = res.status;
    throw err;
  }
  return data?.order || null;
}

export function opsOrderReportFileUrl({ orderId, reportIndex, download = false }) {
  const qs = download ? '?download=1' : '';
  return `${API_BASE}/api/ops/orders/${encodeURIComponent(orderId)}/reports/${reportIndex}/file${qs}`;
}

export async function openOpsOrderReport({ orderId, reportIndex }) {
  const authToken = mustToken();
  const res = await fetch(opsOrderReportFileUrl({ orderId, reportIndex }), {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!res.ok) {
    const data = await parseJsonSafe(res);
    throw new Error(data?.error || `Failed to open report (HTTP ${res.status}).`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export async function downloadOpsOrderReport({ orderId, reportIndex, fileName }) {
  const authToken = mustToken();
  const res = await fetch(opsOrderReportFileUrl({ orderId, reportIndex, download: true }), {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!res.ok) {
    const data = await parseJsonSafe(res);
    throw new Error(data?.error || `Download failed (HTTP ${res.status}).`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'report.pdf';
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export function opsOrderPrescriptionFileUrl({ orderId, download = false }) {
  const qs = download ? '?download=1' : '';
  return `${API_BASE}/api/ops/orders/${encodeURIComponent(orderId)}/prescription${qs}`;
}

export async function openOpsOrderPrescription({ orderId }) {
  const authToken = mustToken();
  const res = await fetch(opsOrderPrescriptionFileUrl({ orderId }), {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!res.ok) {
    const data = await parseJsonSafe(res);
    throw new Error(data?.error || `Failed to open prescription (HTTP ${res.status}).`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export async function downloadOpsOrderPrescription({ orderId, fileName }) {
  const authToken = mustToken();
  const res = await fetch(opsOrderPrescriptionFileUrl({ orderId, download: true }), {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!res.ok) {
    const data = await parseJsonSafe(res);
    throw new Error(data?.error || `Download failed (HTTP ${res.status}).`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'prescription';
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export async function createOpsVoc({ token, orderId, type, subject, description }) {
  const data = await opsFetch('/api/ops/voc', {
    token,
    method: 'POST',
    body: { orderId, type, subject, description },
  });
  return data?.voc || null;
}

export async function fetchOpsVoc({ token, status } = {}) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const data = await opsFetch(`/api/ops/voc${qs}`, { token });
  return data?.voc || [];
}

export async function createOpsRefund({ token, orderId, amount, reason }) {
  const data = await opsFetch('/api/ops/refunds', {
    token,
    method: 'POST',
    body: { orderId, amount, reason },
  });
  return data?.refund || null;
}

export async function fetchOpsRefunds({ token } = {}) {
  const data = await opsFetch('/api/ops/refunds', { token });
  return data?.refunds || [];
}

export async function createOpsEscalation({ token, orderId, reason, ownerEmail }) {
  const data = await opsFetch('/api/ops/escalations', {
    token,
    method: 'POST',
    body: { orderId, reason, ownerEmail },
  });
  return data?.escalation || null;
}

export async function fetchOpsEscalations({ token, status } = {}) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const data = await opsFetch(`/api/ops/escalations${qs}`, { token });
  return data?.escalations || [];
}

export async function fetchOpsLabPayments({ token, paymentStatus, page, limit } = {}) {
  const qs = new URLSearchParams();
  if (paymentStatus) qs.set('paymentStatus', paymentStatus);
  if (page) qs.set('page', String(page));
  if (limit) qs.set('limit', String(limit));
  const data = await opsFetch(`/api/ops/payments/lab?${qs.toString()}`, { token });
  return { payments: data?.payments || [], pagination: data?.pagination || null };
}

export async function fetchOpsOneMgRedirects({ service = 'all', q = '', page = 1, limit = 25 } = {}) {
  const qs = new URLSearchParams();
  if (service) qs.set('service', service);
  if (q) qs.set('q', q);
  if (page) qs.set('page', String(page));
  if (limit) qs.set('limit', String(limit));
  const data = await opsFetch(`/api/ops/1mg-redirects?${qs.toString()}`);
  return {
    redirects: data?.redirects || [],
    pagination: data?.pagination || null,
  };
}

export async function patchOpsOneMgRedirectNote({ id, opsNote }) {
  const data = await opsFetch(`/api/ops/1mg-redirects/${encodeURIComponent(id)}/note`, {
    method: 'PATCH',
    body: { opsNote },
  });
  return data?.redirect || null;
}

export async function fetchOpsSponsors({ token } = {}) {
  const data = await opsFetch('/api/ops/sponsors', { token });
  return data?.sponsors || [];
}

export async function fetchOpsPartners({ token, type }) {
  const data = await opsFetch(`/api/ops/partners/${encodeURIComponent(type)}`, { token });
  return {
    partners: data?.partners || [],
    error: data?.error || null,
    onboardingDashboardUrl: data?.onboardingDashboardUrl || '',
  };
}

export const SERVICE_AREA_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'coming_soon', label: 'Coming soon' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'not_available', label: 'Not available' },
];

export const SERVICE_LABELS = {
  dental_consult: 'Dental consult',
  dental_test: 'Dental test',
  lab_test: 'Lab test',
  clinic_appointment: 'Clinic appointment',
  medicine: 'Pharmacy',
  online_consult: 'Tele Doctor consultation',
  mental_wellness: 'Wellness benefits',
};

export function formatServiceType(value) {
  return SERVICE_LABELS[value] || String(value || '').replace(/_/g, ' ');
}

export function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function slaBadgeClass(slaStatus) {
  if (slaStatus === 'breached') return 'ops-badge-suspended';
  if (slaStatus === 'at_risk') return 'ops-badge-coming_soon';
  return 'ops-badge-active';
}

export async function fetchOpsLogCategories() {
  const data = await opsFetch('/api/ops/logs/categories');
  return data?.categories || [];
}

export async function fetchOpsLogDates() {
  const data = await opsFetch('/api/ops/logs/dates');
  return data?.dates || [];
}

export async function fetchOpsActivityLogDates(category) {
  const params = new URLSearchParams({ category });
  const data = await opsFetch(`/api/ops/logs/dates?${params.toString()}`);
  return data?.dates || [];
}

export async function fetchOpsLogs({ date, type = 'app', limit = 50, offset = 0, level = '', q = '' } = {}) {
  const params = new URLSearchParams({
    date,
    type,
    limit: String(limit),
    offset: String(offset),
  });
  if (level) params.set('level', level);
  if (q) params.set('q', q);
  const data = await opsFetch(`/api/ops/logs?${params.toString()}`);
  return {
    entries: data?.entries || [],
    total: data?.total || 0,
    date: data?.date || date,
    type: data?.type || type,
  };
}

export async function fetchOpsActivityLogs({
  category = 'ops',
  date,
  limit = 50,
  offset = 0,
  level = '',
  q = '',
  decision = '',
} = {}) {
  const params = new URLSearchParams({
    category,
    date,
    limit: String(limit),
    offset: String(offset),
  });
  if (level) params.set('level', level);
  if (q) params.set('q', q);
  if (decision) params.set('decision', decision);
  const data = await opsFetch(`/api/ops/logs/activity?${params.toString()}`);
  return {
    entries: data?.entries || [],
    total: data?.total || 0,
    date: data?.date || date,
    category: data?.category || category,
  };
}
